import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type ComponentType } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, MapPin, Share2, Ticket } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { formatAppDateTime, translateDiscoveryCategory } from "@pulse/shared";
import { AiImageBadge } from "@/components/AiImageBadge";
import { LoadingBlock, ErrorBlock } from "@/components/AsyncState";
import { CardInterestButton } from "@/components/CardInterestButton";
import { CardProposeButton } from "@/components/CardProposeButton";
import { DetailBackButton, useDetailBack } from "@/components/DetailBackButton";
import { EventBadges } from "@/components/EventBadges";
import { EventImage } from "@/components/EventImage";
import { EventScheduleLabel } from "@/components/EventScheduleLabel";
import { ReportWrongDataButton } from "@/components/FeedbackSheet";
import { EntityRatingSection } from "@/components/ratings/EntityRatingSection";
import { WeatherWidget } from "@/components/WeatherWidget";
import { useShareEvent } from "@/hooks/useShareEvent";
import { useAuth } from "@/lib/auth";
import {
  capacityFillPercent,
  isSellingFast,
  ticketsRemaining,
} from "@/lib/badges";
import { useCheckinCounts, useCheckinToggle, useUserCheckin } from "@/lib/checkins";
import { detailHeroViewTransitionStyle } from "@/lib/depthPushTransition";
import { logEventDetailView } from "@/lib/engagementEvents";
import { extractEventDateKey, zurichDateKey } from "@/lib/eventSchedule";
import {
  enrichEventsWithUserDistance,
  getEventDistanceLabel,
  useEvent,
} from "@/lib/events";
import { resolveEventPriceDisplay, useEventTicketTypes } from "@/lib/eventTicketTypes";
import { isCityCenterFallbackCoords } from "@/lib/geo";
import { useUserInterestedEvents } from "@/lib/interested";
import { useLocation } from "@/lib/location";
import { fetchFriendsAttending } from "@/lib/recommendations";
import { cn } from "@/lib/utils";
import {
  getDresscodeForCategory,
  getEventDescription,
  googleMapsNavigationUrlForVenue,
} from "@/lib/venueDisplay";
import { getVibeInfo } from "@/lib/vibe";

type VenueLocationMapProps = {
  lat: number;
  lng: number;
  label: string;
  className?: string;
};

export const Route = createFileRoute("/event/$id")({
  component: EventDetail,
});

function EventDetail() {
  const { id } = Route.useParams();
  const { data: event, isLoading, isError, error, refetch } = useEvent(id);
  const { data: ticketTypes = [] } = useEventTicketTypes(id);
  const { t, i18n } = useTranslation("consumer");
  const goBack = useDetailBack();
  const { location, userLat, userLng, locationSource } = useLocation();
  const { user, isGuest } = useAuth();
  const userId = user && !isGuest ? user.id : undefined;
  const { data: interestedSet } = useUserInterestedEvents([id]);
  const { data: checkinCounts = new Map<string, number>() } = useCheckinCounts([id]);
  const checkinCount = checkinCounts.get(id) ?? 0;
  const { data: isCheckedIn = false, isLoading: checkinLoading } = useUserCheckin(id);
  const { canCheckIn, isToggling, toggleError, toggle } = useCheckinToggle(id);
  const vibe = getVibeInfo(checkinCount, event?.ticketsSold ?? 0, event?.capacity ?? 0);
  const { execute: shareEvent, message: shareStatus } = useShareEvent(event);
  const [VenueLocationMap, setVenueLocationMap] = useState<ComponentType<VenueLocationMapProps> | null>(
    null,
  );

  const { data: friendsAttending = 0 } = useQuery({
    queryKey: ["friendsAttending", userId, id],
    queryFn: async () => {
      const map = await fetchFriendsAttending(userId!, [id]);
      return map.get(id) ?? 0;
    },
    enabled: !!userId,
    staleTime: 60_000,
  });

  useEffect(() => {
    import("@/components/VenueLocationMap").then((m) => {
      setVenueLocationMap(() => m.VenueLocationMap);
    });
  }, []);

  useEffect(() => {
    if (!event || !userId) return;
    logEventDetailView(event.id, userId);
  }, [event?.id, userId]);

  const distanceLabel = useMemo(() => {
    if (!event) return null;
    const [enriched] = enrichEventsWithUserDistance([event], event.city || location, userLat, userLng);
    if (!enriched) return null;
    return getEventDistanceLabel(enriched);
  }, [event, location, userLat, userLng]);

  const mapsUrl = useMemo(() => {
    if (!event) return "#";
    const target = {
      lat: event.lat,
      lng: event.lng,
      name: event.venue,
      city: event.city,
      address: event.venueAddress,
    };
    if (locationSource === "gps" && userLat != null && userLng != null) {
      return googleMapsNavigationUrlForVenue(target, { lat: userLat, lng: userLng });
    }
    return googleMapsNavigationUrlForVenue(target);
  }, [event, locationSource, userLat, userLng]);

  const galleryImages = useMemo(() => {
    if (!event) return [];
    const urls = [event.image, ...(event.imageAlternates ?? []), ...event.gallery].filter(Boolean);
    return [...new Set(urls)];
  }, [event]);

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <LoadingBlock label={t("event.loading")} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="px-5 pt-20">
        <ErrorBlock
          message={error instanceof Error ? error.message : t("event.loadError")}
          onRetry={() => refetch()}
        />
        <button
          type="button"
          onClick={goBack}
          className="mt-4 block w-full text-center text-sm text-pulse"
        >
          {t("event.backToDiscover")}
        </button>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="grid min-h-screen place-items-center px-6 text-center">
        <div>
          <p className="text-muted-foreground">{t("event.notFound")}</p>
          <button type="button" onClick={goBack} className="mt-3 inline-block text-pulse">
            {t("event.backToDiscover")}
          </button>
        </div>
      </div>
    );
  }

  if (event.isTeaser) {
    const unlockLabel = event.unlockAtIso
      ? formatAppDateTime(new Date(event.unlockAtIso), i18n.language, {
          dateStyle: "medium",
          timeStyle: "short",
        })
      : null;
    return (
      <div className="mx-auto flex min-h-screen max-w-lg flex-col px-5 pb-16 pt-14">
        <button
          type="button"
          onClick={goBack}
          className="mb-6 inline-flex items-center gap-2 text-sm text-white/60"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("event.backToDiscover")}
        </button>
        <div className="overflow-hidden rounded-2xl border border-white/10 bg-[#141416]">
          <div className="relative aspect-[16/10] bg-[#1a1a1c]">
            {event.image ? (
              <img
                src={event.image}
                alt=""
                className="h-full w-full object-cover blur-md scale-105"
              />
            ) : null}
            <div className="absolute inset-0 bg-black/55" />
          </div>
          <div className="space-y-3 px-5 py-6 text-center">
            <p className="text-xs uppercase tracking-[0.18em] text-white/45">
              {translateDiscoveryCategory(event.category, t)}
            </p>
            <h1 className="text-xl text-[#F4F1EA]">
              {unlockLabel
                ? t("event.unlockSoon", { date: unlockLabel })
                : t("event.unlockPending")}
            </h1>
            <p className="text-sm text-white/55">{t("event.unlockHint")}</p>
            <Link
              to="/user/login"
              className="mt-2 inline-flex rounded-full bg-pulse px-5 py-2.5 text-sm font-semibold text-black"
            >
              {t("event.seePlans")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const price = resolveEventPriceDisplay(
    event.entryPrice,
    event.currency,
    ticketTypes,
    t("event.free"),
  );
  const displayDescription =
    getEventDescription(event) ?? t("event.eventAtVenue", { venue: event.venue });
  const remaining = ticketsRemaining(event.ticketsSold, event.capacity);
  const sellingFast = isSellingFast(event.ticketsSold, event.capacity);
  const fillPct = capacityFillPercent(event.ticketsSold, event.capacity);
  const isInterested = interestedSet?.has(event.id) ?? false;
  const interestedTonight = Math.max(event.savedCount, 0);
  const hasCoords = event.lat != null && event.lng != null;
  const showPreciseMap =
    hasCoords && !isCityCenterFallbackCoords(event.lat, event.lng, event.city || location);
  const showWeather = event.isOutdoor && hasCoords;
  const dresscode =
    event.dressCode && event.dressCode !== "—"
      ? event.dressCode
      : getDresscodeForCategory(event.category);
  const genreLabel =
    event.storedMusicStyle ??
    event.storedGenres[0] ??
    event.genres[0] ??
    null;
  const categoryLabel = translateDiscoveryCategory(event.category, t);
  const heroBadgeCategory = genreLabel ?? categoryLabel;

  const handleTicketClick = () => {
    toast.info(t("event.ticketingComingSoon"));
  };

  const ctaPriceLabel = price.kind === "hidden" ? null : price.label;

  return (
    <div className="pulse-detail-body-fade min-h-screen bg-background pb-[calc(env(safe-area-inset-bottom,0px)+11.5rem)]">
      <div className="venue-detail-hero" style={detailHeroViewTransitionStyle()}>
        <EventImage
          src={event.image}
          alt={event.name}
          className="venue-detail-hero__image"
          preset="hero"
          fetchPriority="high"
        />
        {event.image ? <AiImageBadge /> : null}
        <div className="venue-detail-hero__top-fade" aria-hidden />
        <div className="venue-detail-hero__bottom-fade" aria-hidden />

        <div className="venue-detail-hero__nav">
          <DetailBackButton
            className="venue-detail-hero__icon-btn"
            aria-label={t("actions.back", { ns: "common" })}
          />
          <div className="venue-detail-hero__nav-actions">
            <ReportWrongDataButton
              defaultCategory="wrong_event_info"
              context={{ event_id: event.id, path: `/event/${event.id}` }}
              className="venue-detail-hero__icon-btn"
            />
            <button
              type="button"
              onClick={() => void shareEvent()}
              aria-label={t("actions.share", { ns: "common" })}
              className="venue-detail-hero__icon-btn"
            >
              <Share2 className="h-4 w-4" />
            </button>
            <CardInterestButton
              targetId={event.id}
              isInterested={isInterested}
              targetType="event"
              className="venue-detail-hero__icon-btn !h-[38px] !w-[38px] !border-white/14 !bg-[rgba(8,8,10,0.45)] !text-[var(--vc-platinum,#edeef1)]"
            />
          </div>
        </div>

        <EventBadges
          event={event}
          surface="hero"
          categoryLabel={heroBadgeCategory}
          cancelledLabel={event.status === "cancelled" ? t("event.cancelled") : null}
        />

        <div className="venue-detail-hero__content">
          <p className="venue-detail-hero__cat">
            {categoryLabel}
            {event.venue ? ` · ${event.venue}` : ""}
          </p>
          <h1 className="venue-detail-hero__title">{event.name}</h1>
          <div className="venue-detail-hero__meta">
            <EventScheduleLabel startsAtIso={event.startsAtIso} />
            {event.ends && event.ends !== "—" ? <span> – {event.ends}</span> : null}
            {distanceLabel ? (
              <>
                <span className="venue-detail-hero__meta-sep" aria-hidden>
                  ·
                </span>
                <span>{distanceLabel}</span>
              </>
            ) : event.city ? (
              <>
                <span className="venue-detail-hero__meta-sep" aria-hidden>
                  ·
                </span>
                <span>{event.city}</span>
              </>
            ) : null}
          </div>
        </div>
      </div>

      <div className="venue-detail-quick">
        {price.kind !== "hidden" ? (
          <div className="venue-detail-chip">
            <span className="venue-detail-chip__lbl">{t("event.priceLabel")}</span>
            <span className="venue-detail-chip__val">{price.label}</span>
          </div>
        ) : null}
        {dresscode ? (
          <div className="venue-detail-chip">
            <span className="venue-detail-chip__lbl">{t("event.dressCode")}</span>
            <span className="venue-detail-chip__val">{dresscode}</span>
          </div>
        ) : null}
        {genreLabel ? (
          <div className="venue-detail-chip">
            <span className="venue-detail-chip__lbl">{t("event.genreLabel")}</span>
            <span className="venue-detail-chip__val">{genreLabel}</span>
          </div>
        ) : null}
        {fillPct != null ? (
          <div className="venue-detail-chip">
            <span className="venue-detail-chip__lbl">{t("event.capacityLabel")}</span>
            <span className="venue-detail-chip__val">{fillPct}%</span>
          </div>
        ) : null}
      </div>

      {sellingFast && remaining > 0 ? (
        <p className="venue-detail-crowd mx-[22px] mb-4 !mt-0">
          {t("event.sellingFast")}
          <span>{t("event.sellingFastHint", { count: remaining })}</span>
        </p>
      ) : null}

      {event.lineup && event.lineup.length > 0 ? (
        <>
          <div className="section px-[22px] pb-0 pt-1">
            <h2
              className="mb-2.5"
              style={{
                fontFamily: "var(--vc-font-display, Fraunces, serif)",
                fontWeight: 500,
                fontSize: 17,
                color: "var(--vc-platinum, #edeef1)",
              }}
            >
              {t("event.lineup")}
            </h2>
          </div>
          <div className="event-detail-lineup">
            {event.lineup.map((artist) => (
              <span key={artist} className="event-detail-lineup__pill">
                {artist}
              </span>
            ))}
          </div>
        </>
      ) : null}

      {(interestedTonight > 0 || checkinCount > 0 || friendsAttending > 0) && (
        <div className="event-detail-social">
          <div className="event-detail-social__text">
            <p className="event-detail-social__t1">
              {t("event.socialStats", {
                interested: interestedTonight,
                checkins: checkinCount,
              })}
            </p>
            {friendsAttending > 0 ? (
              <p className="event-detail-social__t2">
                {t("event.friendsPlanning", { count: friendsAttending })}
              </p>
            ) : null}
          </div>
        </div>
      )}

      <div className="mt-2 space-y-6 px-5">
        <div className="flex flex-col rounded-3xl border border-border bg-surface p-5 shadow-card">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-bold">{t("event.about")}</h2>
              {event.venueId ? (
                <Link
                  to="/venue/$id"
                  params={{ id: event.venueId }}
                  className="mt-1 block text-sm font-medium text-pulse hover:underline"
                >
                  {event.venue}
                </Link>
              ) : (
                <p className="mt-1 text-sm text-muted-foreground">{event.venue}</p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="In Google Maps öffnen"
                className="grid h-8 w-8 place-items-center rounded-full border border-white/10 bg-black/40 text-pulse"
              >
                <MapPin className="h-5 w-5" />
              </a>
              <CardProposeButton
                target={{
                  kind: "event",
                  eventId: event.id,
                  title: event.name,
                  defaultDate: extractEventDateKey(event.startsAtIso) ?? zurichDateKey(),
                }}
              />
            </div>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{displayDescription}</p>

          {(event.venueAddress || distanceLabel) && (
            <div className="mt-4 border-t border-border pt-4">
              <h3 className="text-sm font-bold">{t("event.address")}</h3>
              {event.venueAddress && (
                <p className="mt-1.5 text-sm text-muted-foreground">{event.venueAddress}</p>
              )}
              {distanceLabel && (
                <p className={cn("text-sm text-muted-foreground", event.venueAddress && "mt-1")}>
                  {t("event.distanceAway", { distance: distanceLabel })}
                </p>
              )}
            </div>
          )}
        </div>

        {shareStatus && (
          <p className="rounded-2xl border border-pulse/30 bg-pulse/10 px-4 py-2 text-center text-xs text-pulse">
            {shareStatus}
          </p>
        )}

        <section className="rounded-2xl border border-border bg-surface p-4">
          <h2 className="text-lg font-bold">{t("event.liveVibeWeather")}</h2>
          <div
            className={cn(
              "mt-3 flex gap-4",
              showWeather ? "flex-col sm:flex-row sm:items-start" : "",
            )}
          >
            <div className={cn("min-w-0", showWeather ? "flex-1" : "w-full")}>
              <p className="text-sm font-bold">
                {vibe.emoji} {t(`vibe.status.${vibe.level}`)}
              </p>
              <div className="mt-2 space-y-0.5 text-xs text-muted-foreground">
                <p>👥 {t("event.checkedInCount", { count: checkinCount })}</p>
                <p>{t("event.ticketsSoldToday", { count: event.ticketsSold })}</p>
                {interestedTonight > 0 && (
                  <p>🔥 {t("event.interestedCount", { count: interestedTonight })}</p>
                )}
              </div>
              {event.live && (
                <div className="mt-3">
                  {canCheckIn ? (
                    <button
                      type="button"
                      disabled={isToggling || checkinLoading}
                      onClick={() => void toggle(isCheckedIn)}
                      className="rounded-2xl px-4 py-2.5 text-sm font-bold transition-all"
                      style={{
                        background: isCheckedIn
                          ? "var(--color-surface-elevated)"
                          : "var(--gradient-pulse)",
                        color: isCheckedIn
                          ? "var(--color-foreground)"
                          : "var(--color-pulse-foreground)",
                        border: isCheckedIn ? "1px solid var(--color-border)" : "none",
                      }}
                    >
                      {isToggling ? "…" : isCheckedIn ? t("event.checkedIn") : t("event.checkIn")}
                    </button>
                  ) : isGuest ? (
                    <p className="text-xs text-muted-foreground">{t("event.signInCheckIn")}</p>
                  ) : null}
                </div>
              )}
              {toggleError && (
                <p className="mt-2 text-xs text-pulse">
                  {toggleError instanceof Error ? toggleError.message : t("event.checkInFailed")}
                </p>
              )}
            </div>
            {showWeather && (
              <div className="flex-1 border-t border-border pt-3 sm:border-l sm:border-t-0 sm:pl-4 sm:pt-0">
                <WeatherWidget lat={event.lat} lng={event.lng} enabled embedded />
              </div>
            )}
          </div>
        </section>

        {galleryImages.length > 1 && (
          <section>
            <h2
              className="mb-2.5"
              style={{
                fontFamily: "var(--vc-font-display, Fraunces, serif)",
                fontWeight: 500,
                fontSize: 17,
              }}
            >
              {t("event.impressions")}
            </h2>
            <div className="venue-detail-gallery !px-0">
              {galleryImages.slice(1).map((src, i) => (
                <div key={`${src}-${i}`} className="relative shrink-0">
                  <EventImage
                    src={src}
                    alt=""
                    className="venue-detail-gallery__item"
                    preset="thumb"
                  />
                  <AiImageBadge size="sm" />
                </div>
              ))}
            </div>
          </section>
        )}

        {showPreciseMap && (
          <section>
            <h2 className="text-lg font-bold">{t("event.map")}</h2>
            <div className="mt-4">
              {VenueLocationMap ? (
                <VenueLocationMap
                  lat={event.lat!}
                  lng={event.lng!}
                  label={event.venue}
                  className="pulse-map-container h-48 w-full overflow-hidden rounded-2xl border border-border"
                />
              ) : (
                <div className="flex h-48 items-center justify-center rounded-2xl border border-border bg-surface">
                  <LoadingBlock label={t("event.loadingMap")} />
                </div>
              )}
            </div>
          </section>
        )}

        <EntityRatingSection
          entityType="event"
          entityId={event.id}
          entityLabel={event.name}
          pulseRating={event.pulseRating}
          pulseRatingCount={event.pulseRatingCount}
          startsAtIso={event.startsAtIso}
        />
      </div>

      {typeof document !== "undefined" &&
        createPortal(
          <div className="venue-detail-sticky" data-testid="event-cta-dock">
            <button
              type="button"
              data-testid="event-ticket-cta"
              onClick={handleTicketClick}
              aria-label={
                ctaPriceLabel
                  ? `${t("event.buyTickets")} · ${ctaPriceLabel}`
                  : t("event.buyTickets")
              }
              className="venue-detail-sticky__primary"
            >
              <Ticket className="h-4 w-4" aria-hidden />
              {t("nav.tickets")}
              {ctaPriceLabel ? <span className="event-cta-price">{ctaPriceLabel}</span> : null}
            </button>
            <div className="venue-detail-sticky__secondary !p-0 overflow-hidden">
              <CardProposeButton
                target={{
                  kind: "event",
                  eventId: event.id,
                  title: event.name,
                  defaultDate: extractEventDateKey(event.startsAtIso) ?? zurichDateKey(),
                }}
                className="!h-full !w-full !rounded-[100px] !border-0 !bg-transparent"
              />
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
